import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { productFieldConfigApi } from '@/services/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { CogIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ProductFieldConfig {
  id: number;
  field_name: string;
  field_label: string;
  field_type: string;
  category_id?: number | null;
  is_required: boolean;
  is_visible: boolean;
  is_active: boolean;
  display_order: number;
  placeholder?: string;
  help_text?: string;
  validation_rules?: any;
  options?: any[];
  created_at: string;
  updated_at?: string;
}

interface CategoryOption {
  id: number | null;
  name: string;
  label: string;
}

const ProductFieldConfigPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [fields, setFields] = useState<ProductFieldConfig[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingField, setEditingField] = useState<ProductFieldConfig | null>(null);
  const [formData, setFormData] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    category_id: null as number | null,
    is_required: false,
    is_visible: true,
    is_active: true,
    display_order: 0,
    placeholder: '',
    help_text: '',
    validation_rules: {},
    options: [],
  });

  useEffect(() => {
    fetchFields();
    fetchCategories();
  }, []);

  const fetchFields = async () => {
    try {
      setIsLoading(true);
      const response = await productFieldConfigApi.getAll(false);
      setFields(response.data.sort((a: ProductFieldConfig, b: ProductFieldConfig) => a.display_order - b.display_order));
    } catch (error: any) {
      toast.error('Failed to fetch field configurations');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await productFieldConfigApi.getCategoriesForDropdown();
      setCategories(response.data);
    } catch (error: any) {
      toast.error('Failed to fetch categories');
      console.error(error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingField) {
        await productFieldConfigApi.update(editingField.id, formData);
        toast.success('Field configuration updated successfully');
      } else {
        await productFieldConfigApi.create(formData);
        toast.success('Field configuration created successfully');
      }
      
      setShowCreateForm(false);
      setEditingField(null);
      resetForm();
      fetchFields();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save field configuration');
      console.error(error);
    }
  };

  const handleEdit = (field: ProductFieldConfig) => {
    setEditingField(field);
    setFormData({
      field_name: field.field_name,
      field_label: field.field_label,
      field_type: field.field_type,
      category_id: field.category_id || null,
      is_required: field.is_required,
      is_visible: field.is_visible,
      is_active: field.is_active,
      display_order: field.display_order,
      placeholder: field.placeholder || '',
      help_text: field.help_text || '',
      validation_rules: field.validation_rules || {},
      options: field.options || [],
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this field configuration?')) {
      return;
    }
    
    try {
      await productFieldConfigApi.delete(id);
      toast.success('Field configuration deleted successfully');
      fetchFields();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete field configuration');
      console.error(error);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingField(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      field_name: '',
      field_label: '',
      field_type: 'text',
      category_id: null,
      is_required: false,
      is_visible: true,
      is_active: true,
      display_order: 0,
      placeholder: '',
      help_text: '',
      validation_rules: {},
      options: [],
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading field configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Product Field Configuration</h1>
            <p className="mt-2 text-gray-600">Customize which fields appear in product forms</p>
          </div>
          <Button
            onClick={() => {
              setEditingField(null);
              resetForm();
              setShowCreateForm(true);
            }}
            className="flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Field
          </Button>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {editingField ? 'Edit Field Configuration' : 'Create New Field Configuration'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Field Name * {editingField && <span className="text-gray-400">(Cannot be changed)</span>}
                </label>
                <Input
                  type="text"
                  name="field_name"
                  value={formData.field_name}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingField}
                  placeholder="e.g., brand, model, warranty"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Field Label *
                </label>
                <Input
                  type="text"
                  name="field_label"
                  value={formData.field_label}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Brand Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Field Type *
                </label>
                <select
                  name="field_type"
                  value={formData.field_type}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Select</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="date">Date</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  name="category_id"
                  value={formData.category_id === null ? '' : formData.category_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      category_id: value === '' ? null : parseInt(value)
                    }));
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  {categories.map((category) => (
                    <option key={category.id || 'all'} value={category.id === null ? '' : category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Choose "All Categories" for global fields, or select a specific category
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <Input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Placeholder
                </label>
                <Input
                  type="text"
                  name="placeholder"
                  value={formData.placeholder}
                  onChange={handleInputChange}
                  placeholder="e.g., Enter brand name"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Help Text
                </label>
                <textarea
                  name="help_text"
                  value={formData.help_text}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Help text shown to users"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_required"
                  checked={formData.is_required}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Required
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_visible"
                  checked={formData.is_visible}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Visible
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                {editingField ? 'Update Field' : 'Create Field'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Fields List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Field Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fields.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No field configurations found. Create your first field!
                  </td>
                </tr>
              ) : (
                fields.map((field) => (
                  <tr key={field.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{field.field_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{field.field_label}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {field.field_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {field.display_order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          field.is_visible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {field.is_visible ? 'Visible' : 'Hidden'}
                        </span>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          field.is_required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {field.is_required ? 'Required' : 'Optional'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(field)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(field.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductFieldConfigPage;

